// src/pages/CustomFieldsTestPage.tsx
import React, { useState } from 'react';
import CustomFields from '../components/CustomFields/CustomFields';
import { useAuth } from '../contexts/AuthContext';
import { useSocketConnectionId } from '../contexts/SocketContext';

// Mock conversation data for testing
const mockConversation = {
  ConversationId: 162390, // Change this to a real conversation ID
  ConfigValueId: 9, // Task type
  ProjectId: undefined as number | undefined,
  PersonId: 1,
  CoreTaskId: 0,
  ConversationLayers: [{ LayerId: 1, Name: 'General', LayerName: 'General' }],
  ConversationGroups: [],
  OwnedById: 1,
  IsProjectConversation: false,
  HasPhases: false,
  Phases: [],
  layerCustomFields: [],
  LayerCustomFields: [],
};

const CustomFieldsTestPage: React.FC = () =>
{
  const { user } = useAuth();
  const socketConnectionId = useSocketConnectionId();
  const token = user?.access_token || '';
  const [conversation, setConversation] = useState(mockConversation);
  const [conversationId, setConversationId] = useState<string>('162390');
  const [isLoading, setIsLoading] = useState(false);

  // Get API URL from environment or use default
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';

  // Configuration for CustomFields component
  const config = {
    apiUrl: apiUrl,
    authToken: token || '',
    disableAutoSave: false,
    canEdit: true,
    canTemporary: false,
    isNew: false,
    showHeader: true,
    callApi: true,
    highlight: false,
    hasOwnScope: false,
    SocketConnectionId: socketConnectionId,
    onChangeLayers: (_conv: any, _isFromInfoWindow: boolean) => {},
    onChangedProject: (_e: any, _conv: any) => {},
    onChangedSecurityGroups: (_e: any, _conv: any) => {},
    onChangedGroupLayers: (_e: any, _conv: any) => {},
    onCustomFieldUpdated: (_field: any) => {},
  };

  // Load conversation from API
  const loadConversation = async () =>
  {
    if (!conversationId || !token) return;

    setIsLoading(true);
    try
    {
      const response = await fetch(
        `${apiUrl}api/Conversation/GetConversationById?conversationId=${conversationId}&layersToHide=&getAllReplies=false&isConversationDetailsView=true&totalReplies=0`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.ok)
      {
        const data = await response.json();
        setConversation(data);
      }
      else
      {
        console.error('Failed to load conversation:', response.status);
      }
    }
    catch (error)
    {
      console.error('Error loading conversation:', error);
    }
    finally
    {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '20px' }}>CustomFields Test Page</h1>

      {/* Controls */}
      <div
        style={{
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
        }}
      >
        <label>
          Conversation ID:
          <input
            type="text"
            value={conversationId}
            onChange={e => setConversationId(e.target.value)}
            style={{
              marginLeft: '10px',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              width: '120px',
            }}
          />
        </label>
        <button
          onClick={loadConversation}
          disabled={isLoading}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {isLoading ? 'Loading...' : 'Load Conversation'}
        </button>
        <span style={{ marginLeft: 'auto', color: '#666' }}>API: {apiUrl}</span>
      </div>

      {/* Token Status */}
      <div
        style={{
          marginBottom: '20px',
          padding: '10px',
          backgroundColor: token ? '#d4edda' : '#f8d7da',
          borderRadius: '4px',
          color: token ? '#155724' : '#721c24',
        }}
      >
        {token ? '✓ Authenticated' : '✗ Not authenticated - Please login first'}
      </div>

      {/* CustomFields Component */}
      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '20px',
          backgroundColor: 'white',
        }}
      >
        <h2 style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
          CustomFields Component
        </h2>

        {token ? (
          <CustomFields
            key={`cf-${conversation.ConversationId}`}
            config={config}
            item={conversation}
            configValueId={conversation.ConfigValueId}
            projectId={conversation.ProjectId ?? null}
            groups={conversation.ConversationGroups}
            section="test-section"
            isProjectTitle={false}
            hideImageProject={false}
            isProjectView={false}
            isConversationDetailsView={true}
            SocketConnectionId={socketConnectionId}
            onOpenEditHistory={(_customField: any) => {}}
          />
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Please login to test CustomFields component
          </div>
        )}
      </div>

      {/* Debug Info */}
      <details style={{ marginTop: '20px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
          Debug: Conversation Data
        </summary>
        <pre
          style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '400px',
          }}
        >
          {JSON.stringify(conversation, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export default CustomFieldsTestPage;
